import { memo, useCallback } from 'react';

import { BookIcon, Check, EllipsisVertical, Sorting } from '@air/next-icons';
import {
  DropdownMenu,
  DropdownMenuItem,
  type DropdownMenuProps,
  DropdownMenuSeparator,
  DropdownMenuSub,
} from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';

import { LibraryBrowseModal } from '~/components/LibraryBeta/LibraryBrowseModal/LibraryBrowseModal';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';

const SIDEBAR_LIBRARIES_MORE_LOCK_TOKEN = 'sidebar-libraries-more';

import { useWorkspaceSidebarLibrariesStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarLibrariesStore';
import { LibrarySortBy, LibrarySortDirection } from '~/components/v2/store/shared/creareLibrarySortSlice';

const WORKSPACE_SIDEBAR_LIBRARIES_MORE_SORT_BY_OPTIONS = [
  {
    id: `${LibrarySortBy.Title}-${LibrarySortDirection.Asc}`,
    label: 'A to Z',
    sortBy: LibrarySortBy.Title,
    sortDirection: LibrarySortDirection.Asc,
  },
  {
    id: `${LibrarySortBy.Title}-${LibrarySortDirection.Desc}`,
    label: 'Z to A',
    sortBy: LibrarySortBy.Title,
    sortDirection: LibrarySortDirection.Desc,
  },
  {
    id: `${LibrarySortBy.CreatedAt}-${LibrarySortDirection.Desc}`,
    label: 'Newest',
    sortBy: LibrarySortBy.CreatedAt,
    sortDirection: LibrarySortDirection.Desc,
  },
  {
    id: `${LibrarySortBy.CreatedAt}-${LibrarySortDirection.Asc}`,
    label: 'Oldest',
    sortBy: LibrarySortBy.CreatedAt,
    sortDirection: LibrarySortDirection.Asc,
  },
  {
    id: `${LibrarySortBy.CustomSort}-separator`,
    type: 'separator',
  },
  {
    id: `${LibrarySortBy.CustomSort}-${LibrarySortDirection.Desc}`,
    label: 'Custom Sort',
    sortBy: LibrarySortBy.CustomSort,
    sortDirection: LibrarySortDirection.Desc,
  },
];

export type WorkspaceSidebarLibrariesMoreDropdownProps = Pick<DropdownMenuProps, 'onOpenChange'>;

export const WorkspaceSidebarLibrariesMoreDropdown = memo(
  ({ onOpenChange }: WorkspaceSidebarLibrariesMoreDropdownProps) => {
    const librarySortBy = useWorkspaceSidebarLibrariesStore((state) => state.librarySortBy);
    const librarySortDirection = useWorkspaceSidebarLibrariesStore((state) => state.librarySortDirection);
    const setLibrarySortBy = useWorkspaceSidebarLibrariesStore((state) => state.setLibrarySortBy);
    const setLibrarySortDirection = useWorkspaceSidebarLibrariesStore((state) => state.setLibrarySortDirection);
    const [showLibraryBrowseModal] = useAirModal(LibraryBrowseModal);

    const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
      token: SIDEBAR_LIBRARIES_MORE_LOCK_TOKEN,
    });

    const handleOpenChange = useCallback(
      (open: boolean) => {
        handleLockOpenChange(open);
        onOpenChange?.(open);
      },
      [handleLockOpenChange, onOpenChange],
    );

    const onBrowseLibraries = useCallback(() => {
      showLibraryBrowseModal();
    }, [showLibraryBrowseModal]);

    return (
      <DropdownMenu
        onOpenChange={handleOpenChange}
        trigger={
          <IconButton appearance="ghost" color="grey" label="More library actions" size="small">
            <EllipsisVertical className="size-4" />
          </IconButton>
        }
        size="large"
      >
        <DropdownMenuItem
          label="Browse libraries"
          onClick={onBrowseLibraries}
          prefix={<BookIcon className="size-4" />}
        />

        <DropdownMenuSub label="Sort by" prefix={<Sorting className="size-4" />}>
          {WORKSPACE_SIDEBAR_LIBRARIES_MORE_SORT_BY_OPTIONS.map((option) => {
            const isSelected = librarySortBy === option.sortBy && librarySortDirection === option.sortDirection;

            if (option.type === 'separator') {
              return <DropdownMenuSeparator key={option.id} />;
            }

            return (
              <DropdownMenuItem
                key={option.id}
                label={option.label}
                onClick={() => {
                  setLibrarySortBy(option.sortBy ?? LibrarySortBy.Title);
                  setLibrarySortDirection(option.sortDirection ?? LibrarySortDirection.Asc);
                }}
                suffix={isSelected ? <Check className="size-4" /> : null}
              />
            );
          })}
        </DropdownMenuSub>
      </DropdownMenu>
    );
  },
);

WorkspaceSidebarLibrariesMoreDropdown.displayName = 'WorkspaceSidebarLibrariesMoreDropdown';
